import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../components/Icon';
import Seo from '../components/Seo';

const SITE_URL = 'https://bocadelraypath.com';

function Services() {
  const location = useLocation();

  useEffect(() => {
    const id = location.hash.replace('#', '');
    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return;
    el.classList.add('is-targeted');
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const timer = window.setTimeout(() => el.classList.remove('is-targeted'), 2400);
    return () => {
      window.clearTimeout(timer);
      el.classList.remove('is-targeted');
    };
  }, [location.hash]);

  const services = [
    {
      id: 'gi-pathology',
      title: 'GI Pathology',
      page: '/gi-pathology',
      icon: 'microscope',
      description: 'Gastrointestinal pathology is our specialty. Our pathologists read endoscopic biopsies and resections from the esophagus through the colon for GI practices, endoscopy and surgery centers, and hospitals across Florida.',
      details: [
        'Esophagus: Barrett’s esophagus surveillance and dysplasia grading, eosinophilic esophagitis, reflux esophagitis',
        'Stomach: H. pylori gastritis, intestinal metaplasia, gastric polyps',
        'Small bowel: celiac disease and other causes of villous blunting',
        'Colon: adenomas and serrated polyps, Crohn’s disease, ulcerative colitis, microscopic colitis',
        'GI carcinomas and neuroendocrine tumors, with immunohistochemistry when needed to characterize a lesion'
      ]
    },
    {
      id: 'surgical-pathology',
      title: 'Surgical Pathology',
      icon: 'slide',
      description: 'Comprehensive examination of surgical and biopsy specimens across organ systems, with particular depth in gastrointestinal biopsies.',
      details: [
        'Gastrointestinal biopsies and polyps from every level of the digestive tract (see GI Pathology)',
        'Prostate needle biopsies, liver biopsies, and thyroid and bladder specimens',
        'Gynecologic specimens, including LEEP cones, endocervical and endometrial samples, and products of conception',
        'Special stains as needed',
        'Rapid turnaround — 24–48 hours for routine cases, with direct pathologist access for complex ones'
      ],
    },
    {
      id: 'cytopathology',
      title: 'Cytopathology',
      icon: 'droplet',
      description: 'Advanced analysis of cells from body fluids and fine needle aspirations.',
      details: [
        'Fine Needle Aspiration (FNA) evaluation',
        'Body fluid analysis (pleural, peritoneal, cerebrospinal)',
        'Pap smear evaluation and cervical cancer screening',
        'Esophageal brushings and urine cytology',
        'Expert cytotechnologist team'
      ]
    },
    {
      id: 'dermatopathology',
      title: 'Dermatopathology',
      icon: 'layers',
      description: 'Specialized expertise in diagnosing skin disorders and conditions.',
      details: [
        'Comprehensive skin biopsy evaluation',
        'Melanoma and non-melanoma skin cancer diagnosis',
        'Inflammatory skin disease assessment',
        'Immunofluorescence studies when needed',
        'Collaboration with dermatologists for optimal care'
      ]
    },
    {
      id: 'immunohistochemistry',
      title: 'Immunohistochemistry',
      icon: 'target',
      description: 'Advanced diagnostic technique using targeted antibodies for precise diagnosis.',
      details: [
        'Comprehensive antibody panel testing',
        'Tumor marker identification and characterization',
        'Predictive marker analysis for treatment planning',
        'Quality control and validation protocols',
        'Pathologist-directed antibody selection and interpretation'
      ]
    },
    {
      id: 'consultation',
      title: 'Expert Consultation',
      icon: 'phone',
      description: 'Second opinion and consultation services for complex cases.',
      details: [
        'Expert review of challenging cases',
        'Multidisciplinary case conferences',
        'Educational support for healthcare providers',
        'Research collaboration opportunities',
        'Review of outside slides and blocks'
      ]
    }
  ];

  const serviceSchema = services.map((service) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    // A service with its own page is the same entity as that page's Service.
    ...(service.page ? { '@id': `${SITE_URL}${service.page}#service` } : {}),
    name: service.title,
    description: service.description,
    url: service.page ? `${SITE_URL}${service.page}` : `${SITE_URL}/services#${service.id}`,
    provider: {
      '@id': `${SITE_URL}/#organization`
    },
    areaServed: {
      '@type': 'State',
      name: 'Florida'
    }
  }));

  return (
    <div className="services-page">
      <Seo
        title="GI, Surgical & Dermatopathology in Florida"
        path="/services"
        description="GI, surgical and dermatopathology, cytopathology, immunohistochemistry and consultation from board-certified pathologists at our Delray Beach, Florida lab."
        jsonLd={serviceSchema}
      />
      <section className="page-banner">
        <p className="section-label">Our Services</p>
        <h1>GI, Surgical &amp; Dermatopathology Services</h1>
        <p>Comprehensive anatomic pathology services delivered with precision, expertise, and care. Our team of experienced pathologists provides accurate diagnoses to support optimal patient care.</p>
      </section>

      <nav className="jump-nav" aria-label="Services on this page">
        {services.map((service) => (
          <a key={service.id} href={`#${service.id}`}>{service.title}</a>
        ))}
      </nav>

      <section className="services-overview">
        <div className="container">
          <div className="services-grid">
            {services.map((service) => (
              <div key={service.id} className="service-card" id={service.id}>
                <Icon name={service.icon} className="card-icon" />
                <h3>{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <div className="service-details">
                  <h4>What We Offer:</h4>
                  <ul>
                    {service.details.map((detail, index) => (
                      <li key={index}>{detail}</li>
                    ))}
                  </ul>
                </div>
                {service.page ? (
                  <Link to={service.page} className="learn-more">More on {service.title}</Link>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="quality-assurance" id="quality">
        <div className="container">
          <p className="section-label">Why Clinicians Trust Us</p>
          <h2>Quality Assurance & Accreditation</h2>
          <div className="quality-grid">
            <div className="quality-item">
              <Icon name="shield" className="card-icon" />
              <h3>CLIA-Certified &amp; Joint Commission Accredited</h3>
              <p>CLIA-certified (CLIA #10D1012714) and holder of The Joint Commission's Gold Seal of Approval for quality and safety.</p>
            </div>
            <div className="quality-item">
              <Icon name="users" className="card-icon" />
              <h3>Expert Team</h3>
              <p>Our board-certified pathologists bring decades of experience and specialized training to every case.</p>
            </div>
            <div className="quality-item">
              <Icon name="microscope" className="card-icon" />
              <h3>Advanced Technology</h3>
              <p>Equipment and testing protocols are validated and monitored under our quality assurance program.</p>
            </div>
            <div className="quality-item">
              <Icon name="clock" className="card-icon" />
              <h3>Rapid Turnaround</h3>
              <p>Efficient processing and reporting to support timely clinical decision-making.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <p className="section-label">Get Started</p>
          <h2>Ready to Partner With Us?</h2>
          <p>Contact our team to learn more about our services or to discuss how we can support your pathology needs.</p>
          <div className="cta-buttons">
            <Link to="/contact" className="cta-button">Contact Us</Link>
            <a 
              href="https://bdp.labdynamo.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="cta-button secondary"
            >
              Provider Portal
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Services;
